// Imports
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

// UI Imports
import Grid from "@material-ui/core/Grid/Grid";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@mui/material/Button";
import Typography from "@material-ui/core/Typography";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import Tooltip from '@material-ui/core/Tooltip';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import IconArrowBack from "@material-ui/icons/ArrowBack";
import Switch from "@mui/material/Switch";
import { withStyles } from "@material-ui/core/styles";
import styles from "./styles";
import { checkPermission } from "../../common/Sidebar/checkPermission";

// App Imports
import {
  nullToEmptyString,
  capitalizeFirstLetter,
} from "../../../setup/helpers";
import { messageShow } from "../../common/api/actions";
import SectionPaper from "../../common/SectionPaper";
import BrandedLoading from "../../common/BrandedLoading";
import { getCategories } from "../api/actions/query";
import { createOrUpdateCategory } from "../api/actions/mutation";
import routes from "../api/routes";

// Component
class MediaGalleryCategoryCreateOrUpdate extends Component {
  constructor(props) {
    super(props);
    
    this.mediaGalleryCategory = {
      _id: "",
      name: "",
      title: "",
      isPublished: true,
      displayOrientation: "grid",
      descriptionType: "none",
      description: "",
    };

    this.state = {
      isLoading: false,
      isLoadingSubmit: false,
      mediaGalleryCategory: this.mediaGalleryCategory,
      permissions: {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
      }
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { categoryId },
      },
    } = this.props;

    this.verifyPermissions();

    // Get category details if edit
    if (categoryId) {
      this.getCategory(categoryId);
    }
  }

  verifyPermissions = async () => {
    const modulePermissions = await checkPermission("marketing", "mediaGallery");
    this.setState({
      permissions: {
        canView: modulePermissions.canView,
        canCreate: modulePermissions.canCreate,
        canEdit: modulePermissions.canEdit,
        canDelete: modulePermissions.canDelete
      }
    });
  }

  getCategory = async (categoryId) => {
    const { messageShow } = this.props;
    
    this.isLoadingToggle(true);

    try {
      // Get all categories and find the specific one
      const { data } = await getCategories();
      
      if (data.success) {
        const category = data.data.find(cat => cat._id === categoryId);
        if (category) {
          this.setState({
            mediaGalleryCategory: category,
          });
        } else {
          messageShow("Category not found.");
        }
      }
    } catch (error) {
      messageShow("Some error occurred. Please try again.");
    } finally {
      this.isLoadingToggle(false);
    }
  };

  onCreateOrUpdate = async (event) => {
    event.preventDefault();

    const { createOrUpdateCategory, messageShow, history } = this.props;
    const { mediaGalleryCategory } = this.state;

    this.isLoadingSubmitToggle(true);

    try {
      const { data } = await createOrUpdateCategory(mediaGalleryCategory);

      this.isLoadingSubmitToggle(false);

      messageShow(data.message);

      if (data.success) {
        history.push(routes.mediaGalleryList.path);
      }
    } catch (error) {
      this.isLoadingSubmitToggle(false);
      messageShow("Some error occurred. Please try again.");
    }
  };

  isLoadingToggle = (isLoading) => {
    this.setState({
      isLoading,
    });
  };

  isLoadingSubmitToggle = (isLoadingSubmit) => {
    this.setState({
      isLoadingSubmit,
    });
  };

  onChange = ({ target: { name, value, type, checked } }) => {
    const { mediaGalleryCategory } = this.state;

    mediaGalleryCategory[name] =
      type === "checkbox" ? checked :
      name === "name" ? capitalizeFirstLetter(value) : value;

    this.setState({ mediaGalleryCategory });
  };

  render() {
    const {
      match: {
        params: { categoryId },
      },
      classes,
    } = this.props;
    const { 
      mediaGalleryCategory, 
      isLoading, 
      isLoadingSubmit, 
      permissions
    } = this.state;

    return (
      <div>
        <Toolbar className={classes.toolbar}>
          <Link to={routes.mediaGalleryList.path}>
            <IconButton className={classes.menuButton} color="inherit">
              <IconArrowBack />
            </IconButton>
          </Link>
          
          <Typography variant="h6" color="inherit" className={classes.grow}>
            {categoryId ? "Edit" : "Create"} Media Gallery Category
          </Typography>
        </Toolbar>

        <Grid item xs={12} lg={6}>
          <SectionPaper padding>
            {isLoading ? (
              <BrandedLoading />
            ) : (
              <form onSubmit={this.onCreateOrUpdate}>
                {/* Input - Name */}
                <Grid item xs={12}>
                  <TextField
                    name={"name"}
                    value={nullToEmptyString(mediaGalleryCategory.name)}
                    onChange={this.onChange}
                    label={"Category Name"}
                    placeholder={"Enter category name"}
                    required={true}
                    margin={"dense"}
                    autoComplete={"off"}
                    fullWidth
                    autoFocus
                  />
                </Grid>
                {/* Input - Title */}
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    value={nullToEmptyString(mediaGalleryCategory.title)}
                    onChange={this.onChange}
                    label="Category Title"
                    placeholder="Enter title"
                    required
                    margin="dense"
                    fullWidth
                  />
                </Grid>

                {/* Input - Description (Section Header Text) */}
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    value={nullToEmptyString(mediaGalleryCategory.description)}
                    onChange={this.onChange}
                    label="Section Description"
                    placeholder="Enter section description"
                    margin="dense"
                    fullWidth
                    multiline
                    rows={3}
                    helperText="This text will appear as a sub-header for this category on the website."
                  />
                </Grid>

                {/* Input - Published */}
                <Grid item xs={12} style={{ marginTop: 10 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={mediaGalleryCategory.isPublished}
                        onChange={this.onChange}
                        name="isPublished"
                        color="primary"
                      />
                    }
                    label="Published"
                  />
                </Grid>
                {/* Input - Display Orientation */}
                <Grid item xs={12}>
                  <TextField
                    select
                    name="displayOrientation"
                    label="Display Orientation"
                    value={mediaGalleryCategory.displayOrientation}
                    onChange={this.onChange}
                    SelectProps={{ native: true }}
                    margin="dense"
                    fullWidth
                  >
                    <option value="grid">Grid</option>
                    <option value="list">List</option>
                    <option value="carousel">Carousel</option>
                  </TextField>
                </Grid>
                {/* Input - Description Type */}
                <Grid item xs={12}>
                  <TextField
                    select
                    name="descriptionType"
                    label="Description Display Type"
                    value={mediaGalleryCategory.descriptionType}
                    onChange={this.onChange}
                    SelectProps={{ native: true }}
                    margin="dense"
                    fullWidth
                    helperText="Select how the description should be displayed for items in this category."
                  >
                    <option value="none">Normal (Inline)</option>
                    <option value="popup">Popup</option>
                    <option value="full-page">Full Page</option>
                    <option value="both">Popup & Full Page</option>
                  </TextField>
                </Grid>
                {/* Button - Save */}
                <Grid item xs={12} className={classes.buttonsContainer} style={{ marginTop: 20 }}>
                  <Link to={routes.mediaGalleryList.path}>
                    <Tooltip title="Cancel" arrow>
                      <Button 
                        type={"button"} 
                        aria-label={"Close"} 
                        color={"error"} 
                        startIcon={<CancelIcon />}
                      >
                        CANCEL
                      </Button>
                    </Tooltip>
                  </Link>
                  <Tooltip title="Save" arrow> 
                    <Button
                      type={"submit"}
                      aria-label={"Save"}
                      startIcon={<SaveIcon />}
                      color={"primary"}
                      disabled={isLoadingSubmit || (categoryId ? !permissions.canEdit : !permissions.canCreate)}
                    >
                      SAVE
                    </Button>
                  </Tooltip>
                </Grid>
              </form>
            )}
          </SectionPaper>
        </Grid>
      </div>
    );
  }
}

// Component Properties
MediaGalleryCategoryCreateOrUpdate.propTypes = {
  createOrUpdateCategory: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

// Component State
function createOrEditState(state) {
  return {
      mediaGalleryCategory: state.mediaGalleryCategory,
  };
}

export default connect(createOrEditState, {
  createOrUpdateCategory,
  messageShow,
})(withStyles(styles)(MediaGalleryCategoryCreateOrUpdate));